import { useState, useEffect, useCallback } from "react";
import { useAdminApi } from "../pages/admin/adminUtils";
import { formatTime } from "./calendarUtils";
import "./BlocksPanel.css";

const pad = n => String(n).padStart(2, "0");
const ymd = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayStr = () => ymd(new Date());

function expandDates(date, weeks) {
  if (!date) return [];
  const start = new Date(date + "T12:00:00");
  const count = Math.min(52, Math.max(1, parseInt(weeks, 10) || 1));
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i * 7);
    return ymd(d);
  });
}

function fmtDate(str) {
  return new Date(str + "T12:00:00").toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });
}

export default function BlocksPanel() {
  const { API, AUTH } = useAdminApi();
  const [blocks, setBlocks] = useState([]);
  const [form, setForm]     = useState({ date: "", time: "", duration: "60", weeks: "1", note: "" });
  const [error, setError]   = useState("");
  const [saved, setSaved]   = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/blocks/admin?from=${todayStr()}`, { headers: AUTH });
      if (!res.ok) {
        throw new Error(res.status === 404
          ? "Blocks aren't enabled on this studio's server yet."
          : `Could not load blocks (${res.status}).`);
      }
      const data = await res.json();
      setBlocks(data.blocks ?? []);
    } catch (err) {
      setError(err.message);
    }
  }, [API, AUTH]);

  useEffect(() => { load(); }, [load]);

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError("");
    setSaved("");
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const dates = expandDates(form.date, form.weeks);
    if (!dates.length || !form.time || !form.duration) {
      setError("Date, start time and duration are required.");
      return;
    }
    setSaving(true); setError(""); setSaved("");
    try {
      const res = await fetch(`${API}/blocks`, {
        method: "POST",
        headers: { ...AUTH, "Content-Type": "application/json" },
        body: JSON.stringify({ dates, time: form.time, duration: Number(form.duration), note: form.note.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not save block (${res.status}).`);
      const n = data.blocks?.length ?? 0;
      setSaved(`${n} block${n === 1 ? "" : "s"} added. Reload the calendar to see ${n === 1 ? "it" : "them"}.`);
      setForm(f => ({ ...f, date: "", weeks: "1", note: "" }));
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async b => {
    if (!window.confirm(`Remove block on ${fmtDate(b.date)} at ${formatTime(b.time)}?`)) return;
    try {
      const res = await fetch(`${API}/blocks/${b._id}`, { method: "DELETE", headers: AUTH });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `Could not remove block (${res.status}).`);
      }
      setBlocks(prev => prev.filter(x => x._id !== b._id));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="blocks-panel">
      <p className="blocks-panel-intro">
        Hold time on the calendar with no student, pass or price. The title shows on the calendar; leave it blank to show as Private.
      </p>

      <form className="blocks-form" onSubmit={handleSubmit}>
        <label className="blocks-field">
          <span>Date</span>
          <input className="adm-input" type="date" name="date" value={form.date} onChange={handleChange} required />
        </label>
        <label className="blocks-field">
          <span>Start time</span>
          <input className="adm-input" type="time" name="time" step="300" value={form.time} onChange={handleChange} required />
        </label>
        <label className="blocks-field">
          <span>Duration (min)</span>
          <input className="adm-input" type="number" name="duration" min="5" max="1440" step="5" value={form.duration} onChange={handleChange} required />
        </label>
        <label className="blocks-field">
          <span>Repeat weekly (weeks)</span>
          <input className="adm-input" type="number" name="weeks" min="1" max="52" value={form.weeks} onChange={handleChange} />
        </label>
        <label className="blocks-field blocks-field-wide">
          <span>Title (shown on calendar)</span>
          <input className="adm-input" type="text" name="note" value={form.note} onChange={handleChange} placeholder="e.g. Opening Celebration" />
        </label>
        <div className="blocks-actions">
          <button type="submit" className="adm-btn adm-btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Add block"}
          </button>
        </div>
      </form>

      {error && <p className="blocks-error">{error}</p>}
      {saved && <p className="blocks-saved">{saved}</p>}

      <h4 className="blocks-heading">Upcoming blocks</h4>
      {blocks.length === 0 ? (
        <p className="blocks-empty">None.</p>
      ) : (
        <ul className="blocks-list">
          {blocks.map(b => (
            <li key={b._id} className="blocks-row">
              <span className="blocks-when">{fmtDate(b.date)} · {formatTime(b.time)} · {b.duration} min</span>
              <button type="button" className="adm-btn adm-btn-ghost-sm" onClick={() => handleDelete(b)}>Remove</button>
              {b.note && <span className="blocks-note">{b.note}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
